import { useState, useEffect, useMemo, useRef } from "react";
import { TextField, Checkbox } from "@mui/material";
import SelectAutocomplete from "../Input/SelectAutocomplete";
import { SeeMoreFiltersButton, ContainerFilters, TopProjectCheckbox, AnimatedFilter, ToggleIcon } from "./styles/Filters.styled";
import CustomDatePicker from "../Input/DatePicker";
import "./styles/filters.css";

const FieldTextInput = ({ handleFiltersList, headerName, id, value = "" }) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleFiltersList({ [id]: inputValue });
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  return <TextField size="small" variant="outlined" id={`filters-${id}`} label={headerName} value={inputValue} onChange={handleChange} />;
};

const displaySelectField = (field, filters, handleFiltersList, setFilterList, handleProjectDate) => {
  switch (field.type) {
    case "textField":
      return <FieldTextInput id={field.field} key={field.field} headerName={field.headerName} handleFiltersList={handleFiltersList} value={filters[field.field]} />;
    case "selectAutocomplete":
      return <SelectAutocomplete key={field.field} variant="outlined" id={field.field} limitTags={1} label={field.headerName} onChange={setFilterList} fetchAction={field.fetchAction} fieldValue={filters[field.field]}></SelectAutocomplete>;
    case "dateTime":
      return [
        <CustomDatePicker variant="outlined" key={field.field + "-from"} label="Date from" id={field.field + "From"} onChange={handleProjectDate} dateValue={filters.dateFrom || null} />,
        <CustomDatePicker variant="outlined" key={field.field + "-to"} label="Date to" id={field.field + "To"} onChange={handleProjectDate} dateValue={filters.dateTo || null} />,
      ];
    case "checkbox":
      return <TopProjectCheckbox key={field.field} control={<Checkbox id={field.field} onChange={(event) => handleFiltersList({ [field.field]: event.target.checked })} checked={!!filters[field.field]} color="warning" />} label="Top project" />;
    default:
      return null;
  }
};

const displaySeeMoreFiltersButton = (headCells, isOpen, onClickSeeMoreFiltersButton) => {
  const filterListFields = headCells.filter(({ isOnFilter }) => isOnFilter);
  return (
    filterListFields.length > BASE_FILTERS_COUNT && (
      <SeeMoreFiltersButton variant="contained" endIcon={<ToggleIcon $isOpen={isOpen} />} onClick={onClickSeeMoreFiltersButton}>
        {isOpen ? "Less filters" : "More filters"}
      </SeeMoreFiltersButton>
    )
  );
};

const BASE_FILTERS_COUNT = 5;
const EXIT_DURATION = 220;
const EXIT_STAGGER = 35;

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function BasicPopover({ headCells, setFilters, filters, handleFiltersList, getData }) {
  const [isDisplayingAllInputs, setIsDisplayingAllInputs] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const collapseTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(collapseTimeoutRef.current), []);

  const handleSubmit = (event) => {
    event.preventDefault();
    getData();
  };

  const setFilterList = (id, value) => {
    if (value.length) {
      setFilters((prev) => ({ ...prev, [id]: value }));
    } else {
      const newFilter = Object.entries(filters).reduce((acc, filter) => {
        const key = filter[0];
        const filterValue = filter[1];
        if (key !== id) {
          return { ...acc, [key]: filterValue };
        }
        return acc;
      }, {});
      setFilters(newFilter);
    }
  };

  const handleProjectDate = (id, value) => {
    setFilters((prev) => ({ ...prev, [id]: value }));
  };

  const filterListFields = useMemo(() => headCells.filter(({ isOnFilter }) => isOnFilter), [headCells]);

  const renderFields = (fields) => fields.flatMap((field) => [].concat(displaySelectField(field, filters, handleFiltersList, setFilterList, handleProjectDate))).filter(Boolean);
  const baseInputs = renderFields(filterListFields.slice(0, BASE_FILTERS_COUNT));
  const extraInputs = isDisplayingAllInputs ? renderFields(filterListFields.slice(BASE_FILTERS_COUNT)) : [];

  const onClickSeeMoreFiltersButton = () => {
    if (isCollapsing) return;

    if (!isDisplayingAllInputs) {
      setIsDisplayingAllInputs(true);
      return;
    }

    // Let the extra filters play their exit animation before unmounting them
    const exitDuration = prefersReducedMotion() ? 0 : EXIT_DURATION + EXIT_STAGGER * Math.max(extraInputs.length - 1, 0);
    setIsCollapsing(true);
    collapseTimeoutRef.current = setTimeout(() => {
      setIsDisplayingAllInputs(false);
      setIsCollapsing(false);
    }, exitDuration);
  };

  return (
    <ContainerFilters onSubmit={handleSubmit}>
      {baseInputs}
      {extraInputs.map((input, index) => (
        <AnimatedFilter key={input.key} $isLeaving={isCollapsing} $delay={isCollapsing ? (extraInputs.length - 1 - index) * EXIT_STAGGER : index * 60}>
          {input}
        </AnimatedFilter>
      ))}
      {displaySeeMoreFiltersButton(headCells, isDisplayingAllInputs && !isCollapsing, onClickSeeMoreFiltersButton)}
      <button type="submit" style={{ display: "none" }} aria-hidden="true" />
    </ContainerFilters>
  );
}
